import React from "react";
import { DownloadButtons } from "./DownloadButtons";
import { ChevronDown, Zap, WifiOff } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage } from "../i18n/LanguageContext";

export const Hero = () => {
  const { t } = useLanguage();
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-32 pb-20 flex flex-col items-center justify-center overflow-hidden grain"
    >
      {/* Background layers */}
      <div className="absolute inset-0 grid-lines opacity-60" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(255,234,0,0.10), transparent 55%)",
        }}
      />
      {/* Diagonal hazard strip at top */}
      <div className="absolute top-[72px] left-0 right-0 h-[6px] hazard-stripes-anim opacity-90" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full">
        {/* Hero logo mark */}
        <div className="flex justify-center mb-6 rise">
          <Logo size={110} aura testId="hero-logo" />
        </div>

        {/* Tag chip */}
        <div className="flex justify-center mb-8 rise rise-d1">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#FFEA00]/40 bg-black/40 backdrop-blur"
            data-testid="hero-eyebrow"
          >
            <Zap className="w-3.5 h-3.5 text-[#FFEA00] fill-[#FFEA00]" />
            <span className="font-mono text-[11px] tracking-[0.3em] text-[#FFEA00] uppercase">
              {t("hero.eyebrow")}
            </span>
          </div>
        </div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="font-display font-bold text-center text-white text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tight uppercase rise rise-d2"
        >
          {t("hero.headline_line1")}
          <br />
          {t("hero.headline_line2_prefix")}{" "}
          <span className="text-[#FFEA00] neon-glow relative inline-block">
            {t("hero.headline_accent")}
            <span className="absolute -bottom-2 left-0 right-0 h-[6px] hazard-stripes" />
          </span>
          <br />
          {t("hero.headline_line3")}
        </h1>

        {/* Subline */}
        <p
          data-testid="hero-subline"
          className="mt-10 text-center text-base md:text-lg text-[#A3A3A3] max-w-2xl mx-auto leading-relaxed rise rise-d3"
        >
          {t("hero.subtitle")}
        </p>

        {/* CTAs */}
        <div className="mt-12 rise rise-d4">
          <DownloadButtons variant="hero" testIdPrefix="hero" />
        </div>

        {/* Offline badge */}
        <div className="mt-6 flex justify-center rise rise-d4">
          <div
            data-testid="hero-offline-badge"
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#FFEA00] bg-[#FFEA00]/[0.06]"
          >
            <WifiOff className="w-3.5 h-3.5 text-[#FFEA00]" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#FFEA00] uppercase">
              {t("hero.offline_badge")}
            </span>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-20 rise rise-d4">
          <StatsRow />
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#A3A3A3]">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
          {t("hero.scroll_hint")}
        </span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </div>
    </section>
  );
};

const StatsRow = () => {
  const { t } = useLanguage();
  const stats = [
    { key: "ai", value: t("hero.stats.ai.value"), label: t("hero.stats.ai.label") },
    { key: "hours", value: t("hero.stats.hours.value"), label: t("hero.stats.hours.label") },
    { key: "pdf", value: t("hero.stats.pdf.value"), label: t("hero.stats.pdf.label") },
    { key: "cloud", value: t("hero.stats.cloud.value"), label: t("hero.stats.cloud.label") },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1f1f1f] border-2 border-[#1f1f1f]">
      {stats.map((s) => (
        <div
          key={s.key}
          data-testid={`hero-stat-${s.key}`}
          className="bg-[#0F0F0F] px-5 py-6 text-center group hover:bg-[#141414] transition-colors"
        >
          <div className="font-display font-bold text-3xl md:text-4xl text-[#FFEA00] leading-none">
            {s.value}
          </div>
          <div className="mt-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
};
